import { mainApi } from '../../index';
import type { FlowEmail } from '../types';

/**
 * Create or update a flow email
 *
 * @param emailData - The email data to create or update
 * @returns Promise resolving to the created/updated flow email
 */
export async function upsertFlowEmail(emailData: {
    flow_id: number;
    flow_email_id?: number;
    status: string;
    data: {
        delay: number;
        delay_unit: string;
        subject: string;
        body: string;
    };
}): Promise<FlowEmail> {
    const { data } = await mainApi.put<FlowEmail>('v1/flows/flow-email', emailData);
    return data;
}

/**
 * Fetch a specific flow email
 *
 * @param emailId - The ID of the email to fetch
 * @returns Promise resolving to the flow email data
 */
export async function fetchFlowEmail(emailId: number): Promise<FlowEmail> {
    const { data } = await mainApi.get<FlowEmail>(`v1/flows/flow-email/${emailId}`);
    return data;
}

/**
 * Delete a flow email
 *
 * @param emailId - The ID of the email to delete
 * @returns Promise that resolves when email is successfully deleted
 */
export async function deleteFlowEmail(emailId: number): Promise<void> {
    await mainApi.delete(`v1/flows/flow-email/${emailId}`);
}

/**
 * Send a test email
 *
 * @param testData - The test email data including subject, body, and recipient email
 * @returns Promise that resolves when test email is sent
 */
export async function sendTestEmail(testData: {
    data: {
        subject: string;
        body: string;
    };
    email_address: string;
}): Promise<void> {
    await mainApi.post('v1/flows/test-send', testData);
}

/**
 * Fetch email template variables and defaults
 *
 * @param type - The template type (e.g., 'flow_default', 'flow_first_time_email', etc.)
 * @returns Promise resolving to template variables and defaults
 */
export async function fetchEmailTemplateVariables(type: string): Promise<{
    flow_email_template: {
        variables: {
            subject: Array<{ id: string; name: string; required: boolean }>;
            body: Array<{ id: string; name: string; required: boolean }>;
        };
        default: {
            subject: string;
            body: string;
        };
    };
}> {
    const response = await mainApi.get(`v1/pages/email-template-variables/${type}`);
    // API returns { data: { flow_email_template: {...} } }
    return response.data.data;
}
